import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Brand, toINamedDocuments } from '@principle-theorem/principle-core';
import {
  AnyAutomationConfiguration,
  AutomationContext,
  IAutomationConfiguration,
  type IBrand,
  type IPractice,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  type INamedDocument,
  isSameRef,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, type Observable, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AutomationConfigurationService } from '../../services/automation-configuration.service';
import { AutomationConfigurationSort } from './automation-configuration-sort';

interface IPracticeConfigurations {
  practice: INamedDocument<IPractice> | undefined;
  configurations: WithRef<AnyAutomationConfiguration>[];
}

@Component({
  selector: 'pr-automation-configuration-list',
  templateUrl: './automation-configuration-list.component.html',
  styleUrls: ['./automation-configuration-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col' },
  standalone: false,
})
export class AutomationConfigurationListComponent {
  templateScope$ = new ReplaySubject<TemplateScope>(1);
  brand$ = new ReplaySubject<WithRef<IBrand>>(1);
  configs$ = new ReplaySubject<WithRef<AnyAutomationConfiguration>[]>(1);
  practices$: Observable<INamedDocument<IPractice>[]>;
  practiceConfigMaps$: Observable<IPracticeConfigurations[]>;
  noConfigs$: Observable<boolean>;

  @Input()
  set templateScope(scope: TemplateScope) {
    if (scope) {
      this.templateScope$.next(scope);
    }
  }

  @Input()
  set brand(brand: WithRef<IBrand>) {
    if (brand) {
      this.brand$.next(brand);
    }
  }

  @Input()
  set configs(configs: WithRef<AnyAutomationConfiguration>[]) {
    this.configs$.next(configs ?? []);
  }

  constructor(
    private _automationConfiguration: AutomationConfigurationService
  ) {
    this.noConfigs$ = this.configs$.pipe(map((configs) => !configs.length));
    this.practices$ = this.brand$.pipe(
      switchMap((brand) => Brand.practices$(brand)),
      toINamedDocuments()
    );

    this.practiceConfigMaps$ = combineLatest([
      this.configs$,
      this.practices$,
    ]).pipe(
      map(([configs, practices]) => {
        return [
          {
            practice: undefined,
            configurations: AutomationConfigurationSort.sort(
              configs.filter((config) => !config.practiceRef)
            ),
          },
          ...practices.map((practice) => ({
            practice,
            configurations: AutomationConfigurationSort.sort(
              configs.filter((config) =>
                isSameRef(config.practiceRef, practice.ref)
              )
            ),
          })),
        ];
      })
    );
  }

  async edit(notification: WithRef<IAutomationConfiguration>): Promise<void> {
    const scope = await snapshot(this.templateScope$);
    await this._automationConfiguration.editConfig(
      notification,
      AutomationContext.Appointment,
      scope
    );
  }

  async delete(notification: WithRef<IAutomationConfiguration>): Promise<void> {
    await this._automationConfiguration.deleteConfig(notification);
  }

  async openAutomations(
    notification: WithRef<IAutomationConfiguration>
  ): Promise<void> {
    await this._automationConfiguration.openAutomations(notification);
  }

  canTestAutomationConfig(config: WithRef<IAutomationConfiguration>): boolean {
    return this._automationConfiguration.canTestAutomationConfig(
      config as unknown as AnyAutomationConfiguration
    );
  }

  async testAutomationConfig(
    notification: WithRef<IAutomationConfiguration>
  ): Promise<void> {
    const config = notification as unknown as AnyAutomationConfiguration;
    await this._automationConfiguration.testAutomationConfig(
      config,
      TemplateScope.Appointment,
      AutomationContext.Appointment
    );
  }

  async restore(
    notification: WithRef<IAutomationConfiguration>
  ): Promise<void> {
    await this._automationConfiguration.restoreConfig(notification);
  }

  async setActive(
    notification: WithRef<IAutomationConfiguration>,
    isActive: boolean
  ): Promise<void> {
    await this._automationConfiguration.setActive(notification, isActive);
  }
}
